import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon } from 'lucide-react';

// --- REVEAL COMPONENT (Required for Footer Animations) ---
function Reveal({
  children,
  delay = 0,
  className = '',
  width = 'w-full'
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    if (ref.current) observer.observe(ref.current);
    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, []);
  return <div ref={ref} className={`transition-all duration-1000 ease-out transform ${width} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'} ${className}`} style={{
    transitionDelay: `${delay}ms`
  }} data-id="element-46">
      {children}
    </div>;
}

// --- MAIN FOOTER COMPONENT ---
export function Footer() {
  return <footer className="bg-[#496f58] text-white font-sans-brand pt-12" data-id="element-47">
      <Reveal data-id="element-48">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pb-8" data-id="element-49">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-b border-white/20 pb-10" data-id="element-50">
            {/* Company Column */}
            <div data-id="element-51">
              <h4 className="font-bold text-lg mb-4 tracking-wider" data-id="element-52">
                Faith & Fig
              </h4>
              <ul className="space-y-2" data-id="element-53">
                <li data-id="element-54">
                  <a href="https://supplement.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-55">
                    Our Mission
                  </a>
                </li>
                <li data-id="element-56">
                  <a href="https://faithandfig.com/pages/contact" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-57">
                    Contact
                  </a>
                </li>
                {/* UPGRADED POLICIES DROPDOWN */}
                <li data-id="element-58">
                  <div className="relative group" data-id="element-59">
                    <button className="text-white/80 hover:text-white transition-colors text-sm flex items-center gap-1 pb-1" data-id="element-60">
                      Policies
                      <ChevronDownIcon size={10} className="transition-transform duration-200 group-hover:rotate-180" data-id="element-61" />
                    </button>

                    {/* Dropdown Menu - Expands Upwards */}
                    <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-lg shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 transform origin-bottom translate-y-2 group-hover:translate-y-0 z-50" data-id="element-62">
                      <a href="https://faithandfig.com/policies/privacy-policy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-63">
                        Privacy Policy
                      </a>
                      <a href="https://faithandfig.com/policies/refund-policy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-64">
                        Refund Policy
                      </a>
                      <a href="https://faithandfig.com/policies/shipping-policy" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-65">
                        Shipping Policy
                      </a>
                      <a href="https://faithandfig.com/policies/terms-of-service" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#F5F3EE] hover:text-[#496f58] transition-colors" data-id="element-66">
                        Terms of Service
                      </a>
                      {/* Tiny triangle pointer at bottom */}
                      <div className="absolute -bottom-1 left-4 w-2 h-2 bg-white transform rotate-45" data-id="element-67"></div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
            {/* Shop Column */}
            <div data-id="element-68">
              <h4 className="font-bold text-lg mb-4 tracking-wider" data-id="element-69">Shop</h4>
              <ul className="space-y-2" data-id="element-70">
                <li data-id="element-71">
                  <a href="https://pomegranates.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-72">
                    Heart Health
                  </a>
                </li>
                <li data-id="element-73">
                  <a href="https://oliveleaf.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-74">
                    Immune Defense
                  </a>
                </li>
                <li data-id="element-75">
                  <a href="https://frankincense.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-76">
                    Joint Comfort
                  </a>
                </li>
                <li data-id="element-77">
                  <a href="https://trysaffron.faithandfig.com" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-78">
                    Mood & Focus
                  </a>
                </li>
              </ul>
            </div>
            {/* Account Column */}
            <div data-id="element-79">
              <h4 className="font-bold text-lg mb-4 tracking-wider" data-id="element-80">Account</h4>
              <ul className="space-y-2" data-id="element-81">
                <li data-id="element-82">
                  <a href="https://faithandfig.com/apps/my-subscriptions" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-83">
                    Manage Subscriptions
                  </a>
                </li>
                <li data-id="element-84">
                  <a href="https://faithandfig.com/apps/track123" className="text-white/80 hover:text-white transition-colors text-sm" data-id="element-85">
                    Track Order
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6" data-id="element-86">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/70" data-id="element-87">
            <p data-id="element-88">© {new Date().getFullYear()} Faith & Fig. All rights reserved.</p>
          </div>
        </div>
      </Reveal>
    </footer>;
}